import {
  CampaignRecommendationFormState,
  CampaignRecommendationRequest,
} from '@/client/campaigns';
import { useCampaignRecommendation } from '@/hooks/query/campaigns.hook';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { AILoadingProgress } from '@/ui/ai-loading';
import { Dialog } from 'primereact/dialog';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CampaignRecommendations, RecommendationForm } from '.';
import { RECOMMENDATION_CONFIG, THINKING_MESSAGES } from './constants';

const StyledDialog = styled(Dialog)`
  width: ${RECOMMENDATION_CONFIG.MODAL_WIDTH};
  max-width: 90vw;

  @media (max-width: ${RECOMMENDATION_CONFIG.MOBILE_BREAKPOINT}) {
    width: 95vw;
  }
`;

type CampaignRecommendationModalProps = {
  visible: boolean;
  onHide: () => void;
};

export const CampaignRecommendationModal: React.FC<
  CampaignRecommendationModalProps
> = ({ onHide, visible }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [queryParams, setQueryParams] = useState<CampaignRecommendationRequest>(
    {
      count: RECOMMENDATION_CONFIG.DEFAULT_COUNT,
    },
  );
  const [shouldFetch, setShouldFetch] = useState(false);

  const {
    result: data,
    isLoading,
    isFetching,
    remove,
  } = useCampaignRecommendation(currentAccount?.id ?? '', queryParams, {
    enabled: shouldFetch && !!currentAccount?.id,
    onError: useCallback(() => {
      remove();
      setShouldFetch(false);
      toast?.error(
        t('campaign.recommendation.error-title'),
        t('campaign.recommendation.error-message'),
        5000,
      );
    }, [toast, t]),
    onSuccess: useCallback(() => {
      setShouldFetch(false);
    }, []),
  });

  const handleClose = useCallback(() => {
    remove();
    setShouldFetch(false);
    onHide();
  }, [remove, onHide]);

  const handleSubmit = (values: CampaignRecommendationFormState) => {
    // Update query params with fallback to default
    setQueryParams({
      count: values.count ?? RECOMMENDATION_CONFIG.DEFAULT_COUNT,
    });

    // Trigger the query by enabling it
    setShouldFetch(true);
  };

  // Memoized header to prevent unnecessary re-renders
  const dialogHeader = useMemo(
    () => (
      <h1 id="recommendation-modal-title">
        {t('campaign.recommendation.title')}
      </h1>
    ),
    [t],
  );

  // Render content based on state
  const renderContent = () => {
    if (isLoading || isFetching) {
      return (
        <AILoadingProgress isLoading={isLoading} messages={THINKING_MESSAGES} />
      );
    }

    if (data) {
      return <CampaignRecommendations data={data} />;
    }

    return null;
  };

  return (
    <StyledDialog
      blockScroll
      visible={visible}
      header={dialogHeader}
      onHide={handleClose}
      draggable={false}
      data-testid="campaign-recommendation-modal"
      aria-labelledby="recommendation-modal-title"
      aria-describedby="recommendation-modal-content"
    >
      <div id="recommendation-modal-content">
        <div className="mb-4">
          <RecommendationForm
            isLoading={isLoading || isFetching}
            onSubmit={handleSubmit}
          />
        </div>

        {renderContent()}
      </div>
    </StyledDialog>
  );
};
